// Every write of the Worklist, through the platform's CRUD API (`/api/crud/<id>`, D143): one
// record per call. A cell is saved at once and shown at once (optimistic); a failure puts the
// old value back and shows the big red message.
//
// - The list rows carry no version, so the first write of a record reads it (`crudApi.get`);
//   if the field already differs from what the grid showed, someone else changed it. Later
//   writes use the version the last write returned. A 409 on a field nobody else changed is
//   saved again over their version (two people, two cells); on the same field it is the message.
// - Writes of one line run one after another (a new line must exist before its next edit).
// - OUTSIDE-PLATFORM: P4 no batch or transaction: Approve N, Finish and list renames are
//   sequences of calls, six at a time, with a progress toast. A failure stops the
//   rest, names what saved and what did not, and reloads — never retried silently. Running the
//   action again finishes the rest: each step checks what is already there. A line's pick tickets
//   are its baseline (one call with the line, `approvalPlan`); only hand-placed ones (from before
//   WMS grouping), or a line too big for a baseline, cost one call each.
// - No regrouping here (V7 step 2): A2000 splits and merges by WMS #. A save of a line keeps what
//   its carry-over brought (`carryInput`), and a save of a field with a conflict settles it.
// - Every write goes through `logged-write.ts` and states its System log entry (P13).

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  LINE_PICK_TICKETS,
  LIST_FIELD,
  LIST_VALUES,
  REVIEW_LABELS,
  ROUTING_LINES,
  approvalPlan,
  approvedDates,
  asDay,
  fieldChange,
  carryInput,
  encodeConflicts,
  heldPlan,
  isBaselineCopy,
  editSideEffects,
  lineInput,
  poPtText,
  withoutConflict,
  sameListValue,
  undoOfEdit,
  relabelValue,
  statusCategory,
  crudInput,
  momentAt,
  storedLineFields,
  readOnlyRefusal,
} from "@app/shared";
import type {
  ApprovalOverrides,
  AnyListName,
  ChangeSpec,
  CopyWrite,
  CrudEntity,
  FieldChange,
  LinePickTicketRecord,
  LineView,
  ListValueRecord,
  PtView,
  RoutingLineRecord,
  RoutingPayload,
  StatusCategory,
  WarehouseField,
} from "@app/shared";
import { ApiRequestError, crudApi, toast } from "@axny/platform-ui";
import { toastIfReadOnly, writeErrorText } from "./write-error";
import type { Log } from "./logged-write";
import { useLoggedWrites } from "./logged-write";
import { notify, useMessage } from "./message";
import { keyedQueue } from "./queue";

type Rec = Record<string, unknown> & { id: string; rv?: string };
/**
 * One step of a multi-record action, named for the message when it stops part-way. `key` / `keys`:
 * the lines it saves (`keyOf`), marked "saving" on the page until it is done.
 */
interface Job {
  label: string;
  run: () => Promise<unknown>;
  key?: string;
  keys?: readonly string[];
}

/**
 * A line while an action saves it (item 1): saving, saved (the marker fading), or failed; `edited`:
 * a cell's own save landed — the whole row marked 1.5 s, so the person sees where it is.
 */
export type LineState = "saving" | "saved" | "edited" | "failed";
/** How long a saved line keeps its fading marker, an edited one its own, and a failed one its red one. */
const SAVED_MS = 1200;
const EDITED_MS = 1500;
const FAILED_MS = 20_000;
const jobKeys = (j: Job) => j.keys ?? (j.key ? [j.key] : []);

/** A record as the CRUD API wants it (`crudInput`): every field of the entity, a text never null. */
const entityInput = crudInput;

/** A stored value as the grid shows it, for "did someone else change it". */
const shown = (v: unknown) => (v === null || v === undefined ? "" : (asDay(v) ?? String(v)));

class ChangedElsewhere extends Error {}

const isCode = (e: unknown, code: string) => e instanceof ApiRequestError && e.code === code;

/** A save another session made first: a 409, or the record no longer what `expect` said. */
export const lostToAnother = (e: unknown) => e instanceof ChangedElsewhere || isCode(e, "conflict");

/**
 * Local development only (`vite` dev build): `localStorage["routing.failAfter"] = "N"` lets the
 * next N writes through and fails the one after — to see a multi-record action stop part-way.
 */
const FAULT_KEY = "routing.failAfter";
class InjectedFault extends Error {
  constructor() {
    super("Injected fault (routing.failAfter, local only)");
  }
}
function injectFault() {
  if (!import.meta.env.DEV) return;
  let left: string | null;
  try {
    left = localStorage.getItem(FAULT_KEY);
    if (left !== null) localStorage.setItem(FAULT_KEY, String(Number(left) - 1));
    if (left !== null && Number(left) <= 0) localStorage.removeItem(FAULT_KEY);
  } catch {
    return;
  }
  if (left !== null && Number(left) <= 0) throw new InjectedFault();
}

/** Runs jobs `width` at a time; stops starting new ones at the first failure and rethrows it. */
async function pool<T>(jobs: (() => Promise<T>)[], onDone?: (i: number) => void, width = 6): Promise<T[]> {
  const results: T[] = [];
  let next = 0;
  let error: unknown;
  const worker = async () => {
    while (next < jobs.length && error === undefined) {
      const i = next++;
      try {
        results[i] = await jobs[i]!();
        onDone?.(i);
      } catch (e) {
        error ??= e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(width, jobs.length) }, worker));
  if (error !== undefined) throw error;
  return results;
}

/** How a multi-record action that stopped part-way says it: an action, or an undo of one. */
interface PartWay {
  done: string;
  notDone: string;
  again: string;
}
const SAVED: PartWay = {
  done: "saved",
  notDone: "not finished",
  again: "The page now shows what is saved — run it again to finish the rest.",
};
const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "a, b, c and 4 more" */
function names(labels: readonly string[]): string {
  const shownLabels = labels.slice(0, 5).join("; ");
  return labels.length > 5 ? `${shownLabels} and ${labels.length - 5} more` : shownLabels;
}

/** A cell's old value, by its line's stored id. */
interface FieldUndo {
  kind: "field";
  lineId: string;
  field: WarehouseField;
  value: string;
  /** What the save set by itself, put back with it (a Routed status's Routed on): `undoOfEdit`. */
  also: Partial<Record<WarehouseField, string>>;
}
/**
 * One Ctrl+Z: a field back to its value; a batch's (Set value, a fill down, a paste) every line,
 * last first, one after another; or an action undone its own way (an appointment date that scheduled lines).
 */
type UndoStep =
  | FieldUndo
  | { kind: "batch"; steps: FieldUndo[] }
  | { kind: "run"; run: (lines: readonly LineView[]) => Promise<boolean> };

/** How many lines of a batch (`saveMany`) save at once; each line's own saves stay one after another. */
const BATCH_WIDTH = 4;

export type Writer = ReturnType<typeof useWriter>;

/** A line in the System log, as it reads when saved: "TJ MAXX · 2 PTs · PO 20 058230". */
export const lineText = (l: Pick<LineView, "customerName" | "pts" | "pos">) =>
  [
    l.customerName || "—",
    `${l.pts.length} PT${l.pts.length === 1 ? "" : "s"}`,
    l.pos.length ? `PO ${l.pos.slice(0, 2).join(", ")}${l.pos.length > 2 ? ` +${l.pos.length - 2}` : ""}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/** A line's own entry: its id once saved, and how it reads. */
const aboutLine = (line: LineView, spec: Omit<ChangeSpec, "lineText">): ChangeSpec => ({
  ...spec,
  lineText: lineText(line),
});

/** What a change does to a line's stored fields, old → new. */
const lineChanges = (line: LineView, change: Record<string, unknown>): FieldChange[] =>
  Object.entries(change).flatMap(([k, v]) =>
    fieldChange(
      k,
      (line.fields as Record<string, unknown>)[k] ?? (line.stored as Record<string, unknown> | null)?.[k] ?? "",
      v,
    ),
  );

/**
 * Saves running in any writer of the page: a new source load (`useSourceVersion`) waits for them
 * before the pages read again, so a save's own refresh is never overtaken by an older answer.
 */
let savesRunning = 0;
const savesDone = new Set<() => void>();
/** Called once no save runs any more (at once when none does). */
export function afterSaves(run: () => void): void {
  if (savesRunning === 0) run();
  else savesDone.add(run);
}
const saveStarted = () => void savesRunning++;
const saveEnded = () => {
  savesRunning = Math.max(0, savesRunning - 1);
  if (savesRunning > 0) return;
  const waiting = [...savesDone];
  savesDone.clear();
  for (const run of waiting) run();
};

export function useWriter(queryKey: readonly unknown[], who: string, today: string) {
  const queryClient = useQueryClient();
  const message = useMessage();
  const writes = useLoggedWrites();
  /** The last version read or written of each record: `entity/id` → record with `rv`. */
  const versions = useRef(new Map<string, Rec>());
  /** Virtual line key → the id it got when it was first saved. */
  const created = useRef(new Map<string, string>());
  const chains = useRef(keyedQueue());
  const undoStack = useRef<UndoStep[]>([]);
  const [saving, setSaving] = useState(0);
  /**
   * Saves and multi-record actions still running, the refresh after them included: a Schedule
   * pickup is "New pickup", then its rows. While any runs, leaving the page asks first — the
   * browser's own "Leave site?" — so a pickup is not left with no line.
   */
  const inFlight = useRef(0);
  useEffect(() => {
    const ask = (e: BeforeUnloadEvent) => {
      if (inFlight.current > 0) e.preventDefault();
    };
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, []);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  /**
   * Each line an action is saving (item 1), by `keyOf`: shown from the moment its dialog closes
   * until the save is confirmed. A held line (`holding`, an action of several steps) stays
   * "saving" across them; the lines a failed step did not save end "failed".
   */
  const [lineStates, setLineStates] = useState<ReadonlyMap<string, LineState>>(new Map());
  const statesNow = useRef(new Map<string, LineState>());
  const held = useRef(new Map<string, number>());
  const failedNow = useRef(new Set<string>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const markLines = (keys: readonly string[], state: LineState | null) => {
    for (const k of keys) {
      clearTimeout(timers.current.get(k));
      if (state) statesNow.current.set(k, state);
      else statesNow.current.delete(k);
      if (state && state !== "saving")
        timers.current.set(
          k,
          setTimeout(
            () => {
              if (statesNow.current.get(k) !== state) return;
              statesNow.current.delete(k);
              setLineStates(new Map(statesNow.current));
            },
            state === "saved" ? SAVED_MS : state === "edited" ? EDITED_MS : FAILED_MS,
          ),
        );
    }
    setLineStates(new Map(statesNow.current));
  };
  /** A step's lines settled: saved, unless an action holding them goes on. */
  const settleLines = (keys: readonly string[], ok: boolean) => {
    if (!ok) for (const k of keys) failedNow.current.add(k);
    const free = keys.filter((k) => !held.current.get(k));
    if (free.length) markLines(free, ok ? "saved" : "failed");
  };
  const [canUndo, setCanUndo] = useState(false);
  /** Lines being approved (by key): pick tickets done of the total, for the Review chip. */
  const [approving, setApproving] = useState<ReadonlyMap<string, { done: number; total: number }>>(new Map());
  const approvingNow = useRef(new Set<string>());
  // Progress is shown at most every 250 ms: each update re-renders the whole page (every row).
  const progressNow = useRef(new Map<string, { done: number; total: number }>());
  const progressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progress = (key: string, p: { done: number; total: number } | null) => {
    if (p) progressNow.current.set(key, p);
    else progressNow.current.delete(key);
    progressTimer.current ??= setTimeout(() => {
      progressTimer.current = null;
      setApproving(new Map(progressNow.current));
    }, 250);
  };

  const patch = (fn: (p: RoutingPayload) => RoutingPayload) =>
    queryClient.setQueryData<RoutingPayload>(queryKey, (p) => (p ? fn(p) : p));

  /** Reads a record, refusing it when `expect`'s field is no longer what the grid showed. */
  const read = async (entity: string, id: string, expect?: { field: string; value: unknown }) => {
    const rec = await crudApi.get(entity, id);
    if (expect && shown(rec[expect.field]) !== shown(expect.value)) throw new ChangedElsewhere();
    versions.current.set(`${entity}/${id}`, rec);
    return rec;
  };

  const put = async (
    entity: CrudEntity,
    id: string,
    change: Record<string, unknown>,
    log: Log,
    expect?: { field: string; value: unknown },
  ) => {
    const write = async (rec: Rec) => {
      injectFault();
      const saved = await writes.update(entity, id, String(rec.rv), entityInput(entity, { ...rec, ...change }), log);
      versions.current.set(`${entity.id}/${id}`, saved);
      return saved;
    };
    const rec = versions.current.get(`${entity.id}/${id}`) ?? (await read(entity.id, id, expect));
    try {
      return await write(rec);
    } catch (e) {
      versions.current.delete(`${entity.id}/${id}`);
      // Someone saved the line since: if they left this cell alone, ours goes on top of theirs.
      if (isCode(e, "conflict") && expect) return write(await read(entity.id, id, expect));
      throw e;
    }
  };

  const create = async (entity: CrudEntity, input: Record<string, unknown>, log: Log) => {
    injectFault();
    const saved = await writes.create(entity, entityInput(entity, input), log);
    versions.current.set(`${entity.id}/${saved.id}`, saved);
    return saved;
  };

  const remove = async (entity: CrudEntity, id: string, log: Log) => {
    const rec = versions.current.get(`${entity.id}/${id}`) ?? (await read(entity.id, id));
    injectFault();
    await writes.remove(entity, id, String(rec.rv), log);
    versions.current.delete(`${entity.id}/${id}`);
  };

  /**
   * After a write: the saved records into the page's data, so it shows them before the reload —
   * gathered and put in at most every 400 ms, as each change re-works every line on the main
   * thread (with one per write, Approve of 50 lines ran ~2 calls at a time instead of 6). A batch's
   * own changes shown before they save (`patchSoon`) go in the same way, first.
   */
  type Patch = (p: RoutingPayload) => RoutingPayload;
  const kept = useRef<{ lines: Rec[]; copies: Rec[]; drop: string[]; patches: Patch[] }>({
    lines: [],
    copies: [],
    drop: [],
    patches: [],
  });
  const keepTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushKept = () => {
    if (keepTimer.current) clearTimeout(keepTimer.current);
    keepTimer.current = null;
    const { lines, copies, drop, patches } = kept.current;
    if (!lines.length && !copies.length && !drop.length && !patches.length) return;
    kept.current = { lines: [], copies: [], drop: [], patches: [] };
    patch((p0) => {
      const p = patches.reduce((x, fn) => fn(x), p0);
      const byId = new Map(p.lines.map((l) => [l.id, l]));
      for (const id of drop) byId.delete(id);
      for (const l of lines) byId.set(l.id, l as unknown as RoutingLineRecord);
      const copyById = new Map(p.pickTickets.map((c) => [c.id, c]));
      for (const c of copies) copyById.set(c.id, c as unknown as LinePickTicketRecord);
      return { ...p, lines: [...byId.values()], pickTickets: [...copyById.values()] };
    });
  };
  const keep = (lines: Rec[], copies: Rec[] = [], dropLine?: string) => {
    kept.current.lines.push(...lines);
    kept.current.copies.push(...copies);
    if (dropLine) kept.current.drop.push(dropLine);
    keepTimer.current ??= setTimeout(flushKept, 400);
  };
  /** A change shown with the next `flushKept`: a batch's lines, not one re-work of the page each. */
  const patchSoon = (fn: Patch) => {
    kept.current.patches.push(fn);
    keepTimer.current ??= setTimeout(flushKept, 400);
  };

  /** A line's key across its first save: a shown-but-unsaved line keeps its virtual key. */
  const keyOf = (line: LineView) => (line.stored?.id.startsWith("pending:") ? line.stored.id.slice(8) : line.key);

  /**
   * A pick ticket's copy as the page knows it now. A line held by a click may predate a save still
   * running (its copies made since): creating them again would hit the unique index.
   */
  const copyOf = (pt: PtView): LinePickTicketRecord | null =>
    (kept.current.copies.findLast((c) => c.pickTicket === pt.pickTicket) as LinePickTicketRecord | undefined) ??
    queryClient.getQueryData<RoutingPayload>(queryKey)?.pickTickets.find((c) => c.pickTicket === pt.pickTicket) ??
    (pt.copy && !isBaselineCopy(pt.copy) ? pt.copy : null);

  /** A plan's copy writes, `width` at a time; `onDone` after each. */
  const writeCopies = (writes: readonly CopyWrite[], onDone?: () => void, width = 6) =>
    pool(
      writes.map((w) => async () => {
        // The line's own entry covers its pick tickets.
        const log = { partOf: `line ${w.input.lineId}` };
        const rec = w.existing
          ? await put(LINE_PICK_TICKETS, w.existing.id, w.input, log)
          : await create(LINE_PICK_TICKETS, w.input, log);
        onDone?.();
        return rec;
      }),
      undefined,
      width,
    );

  const storedId = (line: LineView) =>
    line.stored && !line.stored.id.startsWith("pending:") ? line.stored.id : created.current.get(keyOf(line));

  /**
   * The stored id of a line, saving it when nobody had touched it — with its pick tickets, as its
   * baseline (one call) or, when that does not fit, as copies. A line the move-over shows Approved
   * or Updated keeps the copies it came with as its baseline (`heldPlan`), unless this save is
   * the Approve itself.
   */
  const ensureStored = async (
    line: LineView,
    change: Partial<RoutingLineRecord>,
    log: Log,
    onWrite?: () => void,
    /** Admin → Import's UPDATED rows: the file's values in the baseline (`approvalPlan`). */
    overrides?: ApprovalOverrides,
  ) => {
    const id = storedId(line);
    if (id) return { id, createdNow: false };
    const held =
      change.reviewStatus !== "Approved" && (line.review === "approved" || line.review === "updated")
        ? heldPlan(line, "")
        : null;
    const plan = held ? { fields: held, writes: [] } : approvalPlan(line, "", copyOf, overrides);
    const rec = await create(ROUTING_LINES, lineInput(line, { ...change, ...plan.fields }), log);
    onWrite?.();
    created.current.set(keyOf(line), rec.id);
    const copies = await writeCopies(
      plan.writes.map((w) => ({ ...w, input: { ...w.input, lineId: rec.id } })),
      onWrite,
    );
    keep([rec], copies, `pending:${keyOf(line)}`);
    return { id: rec.id, createdNow: true };
  };

  /** Writes of one line, one after another. */
  const chained = <T>(key: string, job: () => Promise<T>): Promise<T> => {
    return chains.current(key, job);
  };

  const errorText = (e: unknown) => (e instanceof InjectedFault ? e.message : writeErrorText(e));

  const failed = async (e: unknown) => {
    versions.current.clear();
    if (e instanceof ChangedElsewhere || isCode(e, "conflict")) {
      await message.error(
        "Someone else changed this line",
        "Your change was not saved: someone changed this cell after you opened the page. It now shows their version — check it, then make your change again.",
      );
    } else if (isCode(e, "not_found")) {
      await message.error(
        "Line not found",
        "That line was merged into another line or moved. The page now shows where it is.",
      );
    } else if (!toastIfReadOnly(e)) {
      await message.error("Couldn't save", errorText(e));
    }
    await queryClient.invalidateQueries({ queryKey: ["routing"] });
  };

  const track = async <T>(job: () => Promise<T>): Promise<T> => {
    setSaving((n) => n + 1);
    inFlight.current++;
    saveStarted();
    try {
      const r = await job();
      setLastSavedAt(new Date());
      return r;
    } finally {
      setSaving((n) => n - 1);
      inFlight.current--;
      saveEnded();
    }
  };

  /**
   * A multi-record action: `width` steps at a time (1 when each step needs the one before),
   * progress on screen ("Approving 12 of 50 lines…"), stop at the first failure and say which
   * steps saved. The grid stays usable meanwhile.
   */
  /**
   * The lines of an action of several steps (a new pickup, then its lines), "saving" from before
   * its first step until it ends: then saved, or failed where a step did not save them.
   */
  const holding = async (keys: readonly string[], action: () => Promise<boolean>): Promise<boolean> => {
    for (const k of keys) {
      held.current.set(k, (held.current.get(k) ?? 0) + 1);
      failedNow.current.delete(k);
    }
    markLines(keys, "saving");
    let ok = false;
    try {
      ok = await action();
      return ok;
    } finally {
      for (const k of keys) held.current.set(k, (held.current.get(k) ?? 1) - 1);
      const failed = keys.filter((k) => !ok || failedNow.current.has(k));
      markLines(
        keys.filter((k) => !failed.includes(k)),
        "saved",
      );
      if (failed.length) markLines(failed, "failed");
    }
  };

  const batch = async (label: string, jobs: Job[], width = 6, unit = "", words = SAVED): Promise<boolean> => {
    if (!jobs.length) return true;
    markLines(
      jobs.flatMap(jobKeys).filter((k) => statesNow.current.get(k) !== "saving"),
      "saving",
    );
    inFlight.current++;
    saveStarted();
    const title = (n: number) => `${label} ${n} of ${jobs.length}${unit ? ` ${unit}` : ""}…`;
    const id = jobs.length > 3 ? toast.add({ title: title(0), timeout: 0 }) : undefined;
    const done = new Set<number>();
    try {
      await track(() =>
        pool(
          jobs.map((j) => j.run),
          (i) => {
            done.add(i);
            settleLines(jobKeys(jobs[i]!), true);
            if (id) toast.update(id, { title: title(done.size) });
          },
          width,
        ),
      );
      return true;
    } catch (e) {
      versions.current.clear();
      settleLines(jobs.filter((_, i) => !done.has(i)).flatMap(jobKeys), false);
      if (jobs.length === 1 && words === SAVED) await failed(e);
      else if (done.size === 0 && toastIfReadOnly(e)) return false;
      else {
        const saved = jobs.filter((_, i) => done.has(i)).map((j) => j.label);
        const unfinished = jobs.filter((_, i) => !done.has(i)).map((j) => j.label);
        await message.error(
          `${label} stopped part-way`,
          `${saved.length} of ${jobs.length} ${words.done}; ${unfinished.length} ${words.notDone} — maybe in part (${errorText(e)}). ${words.again}`,
          [
            ...(saved.length ? [`${upper(words.done)}: ${names(saved)}`] : []),
            `${upper(words.notDone)}: ${names(unfinished)}`,
          ],
        );
      }
      return false;
    } finally {
      if (id) toast.close(id);
      flushKept();
      await queryClient.invalidateQueries({ queryKey: ["routing"] }).finally(() => {
        inFlight.current--;
        saveEnded();
      });
    }
  };

  const review = () => ({ reviewStatus: "Approved" as const, reviewedBy: who, reviewedAt: new Date().toISOString() });
  const lineName = (l: LineView) => `${l.customerName} · ${poPtText(l)}`;

  /**
   * One field of one line written, shown at once (the page's data patched) and put back when the save
   * fails, which it throws. Its Undo step and what the save set by itself (`side`) for the caller.
   */
  const writeField = async (
    line: LineView,
    field: WarehouseField,
    value: string,
    action: "edit" | "choose" | "restore",
    note: string,
    also: Partial<Record<WarehouseField, string>>,
    /** How the change shows before it saves: at once (a cell), or with the next `flushKept` (a batch). */
    show: (fn: Patch) => void = patch,
  ) => {
    const old = line.fields[field];
    // A Routing status means what its list's category says (Routed sets Routed on; Cancelled finishes the line).
    const lists = queryClient.getQueryData<RoutingPayload>(queryKey)?.listValues ?? [];
    const side = editSideEffects(line, field, value, who, today, statusCategory(value, lists), momentAt(new Date()));
    // A save keeps the carry-over the line shows; a value in a field the sources disagreed on settles it.
    const settled = line.carry.conflicts[field]
      ? { carryConflicts: encodeConflicts(withoutConflict(line.carry.conflicts, field)) }
      : {};
    const carried = line.carry.sources.length ? carryInput(line) : {};
    // What the change set by itself, and what Undo or Restore sets with it, which wins.
    const more = { ...side, ...also };
    // As stored: the PE fields through `storedLineFields` (PE Order Status never edited is NULL).
    const change = {
      ...carried,
      ...storedLineFields({ [field]: value }),
      ...side,
      ...storedLineFields(also),
      ...settled,
    } as Partial<RoutingLineRecord>;
    const expect = { field, value: (storedLineFields({ [field]: old }) as Record<string, unknown>)[field] ?? null };
    const log = aboutLine(line, {
      action,
      changes: [...fieldChange(field, old, value), ...lineChanges(line, more)],
      ...(note ? { note } : {}),
    });
    const id0 = storedId(line);
    const tempId = `pending:${keyOf(line)}`;
    show((p) =>
      id0
        ? { ...p, lines: p.lines.map((l) => (l.id === id0 ? { ...l, ...change } : l)) }
        : {
            ...p,
            lines: [
              ...p.lines.filter((l) => l.id !== tempId),
              { id: tempId, ...lineInput(line, change) } as RoutingLineRecord,
            ],
          },
    );
    try {
      const id = await chained(keyOf(line), async () => {
        const made = await ensureStored(line, change, log);
        if (!made.createdNow) keep([await put(ROUTING_LINES, made.id, change, log, expect)]);
        return made.id;
      });
      const undo: FieldUndo = { kind: "field", lineId: id, ...undoOfEdit(line, field, side) };
      return { undo, side };
    } catch (e) {
      show((p) => ({
        ...p,
        lines: p.lines
          .filter((l) => l.id !== tempId)
          .map((l) => (line.stored && l.id === line.stored.id ? line.stored : l)),
      }));
      throw e;
    }
  };

  /** `action`: an edit, a "2 values" choice or a Restore (the System log says which). */
  const saveField = async (
    line: LineView,
    field: WarehouseField,
    value: string,
    undoable = true,
    action: "edit" | "choose" | "restore" = "edit",
    note = "",
    /** Other fields saved in the same write (Undo and Restore put a whole change back as one step). */
    also: Partial<Record<WarehouseField, string>> = {},
  ): Promise<boolean> => {
    try {
      const { undo, side } = await track(() => writeField(line, field, value, action, note, also));
      if (undoable) {
        undoStack.current.push(undo);
        setCanUndo(true);
      }
      markLines([keyOf(line)], "edited");
      if (side.finishedStatus === "Cancelled")
        notify(`${line.customerName} is cancelled`, "It left the Worklist and is now in History.");
      return true;
    } catch (e) {
      await failed(e);
      return false;
    }
  };

  /**
   * Several lines' writes of one field (Set value, a fill down, a paste; an Undo of one), as each would
   * be typed: one record and one change entry per line (`note` on each), `BATCH_WIDTH` lines at a time.
   * A failed line does not stop the others: its value is put back and the big red message lists every
   * failed line once, at the end. "All changes saved" waits for the whole batch (`track`, `savesRunning`).
   */
  const writeMany = async (
    items: readonly {
      line: LineView;
      field: WarehouseField;
      value: string;
      also?: Partial<Record<WarehouseField, string>>;
    }[],
    action: "edit",
    note: string,
    title: string,
  ) => {
    const undos: FieldUndo[] = [];
    const cancelled: LineView[] = [];
    const failures: { line: LineView; error: unknown }[] = [];
    const keys = items.map((i) => keyOf(i.line));
    markLines(keys, "saving");
    inFlight.current++;
    // Counted until the page has read again: a new source load waits for it (`afterSaves`).
    saveStarted();
    const label = (n: number) => `${title} ${n} of ${items.length}…`;
    const toastId = items.length > 3 ? toast.add({ title: label(0), timeout: 0 }) : undefined;
    let done = 0;
    let next = 0;
    try {
      await track(async () => {
        const worker = async () => {
          while (next < items.length) {
            const item = items[next++]!;
            try {
              const { undo, side } = await writeField(
                item.line,
                item.field,
                item.value,
                action,
                note,
                item.also ?? {},
                patchSoon,
              );
              undos.push(undo);
              if (side.finishedStatus === "Cancelled") cancelled.push(item.line);
              settleLines([keyOf(item.line)], true);
            } catch (e) {
              // Its version is forgotten already (`put`): an Undo or a retry reads it again.
              failures.push({ line: item.line, error: e });
              settleLines([keyOf(item.line)], false);
            }
            done++;
            if (toastId) toast.update(toastId, { title: label(done) });
          }
        };
        await Promise.all(Array.from({ length: Math.min(BATCH_WIDTH, items.length) }, worker));
      });
    } finally {
      if (toastId) toast.close(toastId);
      flushKept();
      await queryClient.invalidateQueries({ queryKey: ["routing"] }).finally(() => {
        inFlight.current--;
        saveEnded();
      });
    }
    return { undos, cancelled, failures };
  };

  /** The big red message once for a batch's failed lines (the others are saved), each with why. */
  const failedLines = async (title: string, saved: number, failures: readonly { line: LineView; error: unknown }[]) =>
    // Every line refused as read-only, none saved: the toast once (a stale tab), not a list of the same words.
    saved === 0 && failures.length && failures.every((f) => readOnlyRefusal(f.error))
      ? void toastIfReadOnly(failures[0]!.error)
      : message.error(
          title,
          `${saved} saved; ${failures.length} not saved. The page now shows what is saved — check those lines and set them again.`,
          failures.map(({ line, error }) =>
            lostToAnother(error)
              ? `${lineName(line)} — someone else changed it after you opened the page`
              : `${lineName(line)} — ${errorText(error)}`,
          ),
        );

  /** Every stored line whose list field holds one of `values`, set to `to`. The Carrier list fills no line field. */
  const relabel = (list: AnyListName, values: readonly string[], to: string, lines: readonly LineView[]): Job[] => {
    if (list === "Carrier") return [];
    const field = LIST_FIELD[list];
    // Work Needed: the value inside the line's several (`relabelValue`).
    return lines
      .filter((l) => storedId(l) && relabelValue(field, l.fields[field], values, to) !== l.fields[field])
      .map((l) => {
        const next = relabelValue(field, l.fields[field], values, to);
        return {
          label: lineName(l),
          key: keyOf(l),
          run: () =>
            chained(keyOf(l), async () =>
              keep([
                await put(
                  ROUTING_LINES,
                  storedId(l)!,
                  { [field]: next },
                  aboutLine(l, {
                    action: "list",
                    changes: fieldChange(field, l.fields[field], next),
                    note: `${list}: “${values.join("”, “")}” is now “${to}”`,
                  }),
                ),
              ]),
            ),
        };
      });
  };

  return {
    /** The writes themselves, for the Shipping actions (`shipping-writer.ts`): one set of versions and chains. */
    kit: {
      put,
      create,
      remove,
      ensureStored,
      chained,
      keyOf,
      storedId,
      batch,
      holding,
      copyOf,
      writeCopies,
      keep,
      patch,
      who,
      today,
    },
    /** A line's saving state (item 1), by its key before and after its first save. */
    lineState: (line: LineView): LineState | undefined => {
      const direct = lineStates.get(keyOf(line)) ?? lineStates.get(line.key);
      if (direct || !line.stored) return direct;
      for (const [virtual, id] of created.current) if (id === line.stored.id) return lineStates.get(virtual);
      return undefined;
    },
    saving,
    lastSavedAt,
    canUndo,
    /** Pick tickets approved so far of a line being approved; undefined when it is not. */
    approvingOf: (line: LineView) => approving.get(keyOf(line)),

    saveField: (line: LineView, field: WarehouseField, value: string) => saveField(line, field, value),
    /** A "2 values" choice: a save of the field, said as one in the System log. */
    choose: (line: LineView, field: WarehouseField, value: string) => saveField(line, field, value, true, "choose"),
    /** A Restore from the System log: the old value saved again; its entry says Restored. */
    restoreField: (line: LineView, field: WarehouseField, value: string, note: string) =>
      saveField(line, field, value, false, "restore", note),
    /**
     * A Restore of a whole change (System log): every field it changed back in one save — the
     * Routing status first, so what a status sets by itself (Routed on) takes the entry's old value.
     */
    restoreFields: (line: LineView, values: Partial<Record<WarehouseField, string>>, note: string) => {
      const fields = Object.keys(values) as WarehouseField[];
      const first = fields.includes("routingStatus") ? "routingStatus" : fields[0];
      if (!first) return Promise.resolve(true);
      const { [first]: value, ...rest } = values;
      return saveField(line, first, value ?? "", false, "restore", note, rest);
    },

    /**
     * One field set on several lines (Set value, a fill down, a paste): `saves` as each line would be
     * typed, `note` in each line's System log entry ("Set with 13 other lines"), one Undo for all.
     * Cancelled lines are said once ("3 lines are cancelled"). Resolves the lines saved and failed.
     */
    async saveMany(
      field: WarehouseField,
      saves: readonly { line: LineView; value: string }[],
      note: string,
      title: string,
    ) {
      if (!saves.length) return { saved: 0, failed: 0 };
      const { undos, cancelled, failures } = await writeMany(
        saves.map((s) => ({ ...s, field })),
        "edit",
        note,
        title,
      );
      if (undos.length) {
        undoStack.current.push({ kind: "batch", steps: undos });
        setCanUndo(true);
      }
      if (cancelled.length === 1)
        notify(`${cancelled[0]!.customerName} is cancelled`, "It left the Worklist and is now in History.");
      else if (cancelled.length > 1)
        notify(`${cancelled.length} lines are cancelled`, "They left the Worklist and are now in History.");
      if (failures.length) await failedLines(`${title} stopped on some lines`, undos.length, failures);
      else notify(`${title}: ${undos.length} line${undos.length === 1 ? "" : "s"} saved`);
      return { saved: undos.length, failed: failures.length };
    },

    /** Undo of this page's own saves since it opened; a reload starts empty. */
    /** An action Ctrl+Z undoes its own way (item 8: lines an appointment date scheduled go off the pickup again). */
    pushUndo(run: (lines: readonly LineView[]) => Promise<boolean>) {
      undoStack.current.push({ kind: "run", run });
      setCanUndo(true);
    },

    async undo(lines: readonly LineView[]) {
      const step = undoStack.current.pop();
      setCanUndo(undoStack.current.length > 0);
      if (!step) return notify("Nothing to undo");
      if (step.kind === "run") {
        if (await step.run(lines)) notify("Undone");
        return;
      }
      if (step.kind === "batch") {
        // Last saved first; a line no longer on the page is said with the failures.
        const back = [...step.steps].reverse();
        const gone = back.filter((s) => !lines.some((l) => storedId(l) === s.lineId));
        const items = back.flatMap((s) => {
          const line = lines.find((l) => storedId(l) === s.lineId);
          return line ? [{ line, field: s.field, value: s.value, also: s.also }] : [];
        });
        const { failures } = items.length ? await writeMany(items, "edit", "Undo", "Undoing") : { failures: [] };
        if (failures.length || gone.length)
          await message.error(
            "Undo stopped on some lines",
            `${items.length - failures.length} of ${step.steps.length} put back.`,
            [
              ...(gone.length ? [`${gone.length} no longer on this page`] : []),
              ...failures.map(({ line, error }) => `${lineName(line)} — ${errorText(error)}`),
            ],
          );
        else notify("Undone", `${items.length} line${items.length === 1 ? "" : "s"} put back`);
        return;
      }
      const line = lines.find((l) => storedId(l) === step.lineId);
      if (!line) return message.error("Can't undo", "That line is no longer on this page.");
      if (await saveField(line, step.field, step.value, false, "edit", "Undo", step.also)) notify("Undone");
    },

    /** A line already being approved is left out: a second click starts nothing. */
    async approve(lines: readonly LineView[]) {
      const todo = lines.filter((l) => !approvingNow.current.has(keyOf(l)));
      if (!todo.length) return notify("Already approving", "That line is still being saved.");
      // One line with copies to write (too big for a baseline): six at a time; several lines: six
      // lines at a time, each line's copies one by one.
      const width = todo.length === 1 ? 6 : 1;
      const jobs = todo.map((line) => {
        const key = keyOf(line);
        // The calls: the line, and the copies its plan writes (none when the baseline fits).
        const total = 1 + approvalPlan(line, storedId(line) ?? "", copyOf).writes.length;
        let done = 0;
        const tick = () => progress(key, { done: ++done, total });
        approvingNow.current.add(key);
        progress(key, { done, total });
        return {
          label: lineName(line),
          key,
          run: () =>
            chained(key, async () => {
              const log = aboutLine(line, {
                action: "approve",
                changes: fieldChange("reviewStatus", REVIEW_LABELS[line.review], "Approved"),
              });
              // The baseline and the dates as approved, in one save.
              const made = await ensureStored(line, { ...review(), ...approvedDates(line) }, log, tick);
              if (made.createdNow) return;
              const plan = approvalPlan(line, made.id, copyOf);
              // Approving a Split line is allowed; its conflicts stay marked (carryInput keeps them).
              const saved = await put(
                ROUTING_LINES,
                made.id,
                { ...carryInput(line), ...review(), ...approvedDates(line), ...plan.fields },
                log,
              );
              tick();
              keep([saved], await writeCopies(plan.writes, tick, width));
            }),
        };
      });
      try {
        if (await batch("Approving", jobs, 6, "lines"))
          notify(`${todo.length} line${todo.length === 1 ? "" : "s"} approved`);
      } finally {
        for (const l of todo) {
          approvingNow.current.delete(keyOf(l));
          progress(keyOf(l), null);
        }
      }
    },

    async finish(lines: readonly LineView[], status: "Shipped" | "Cancelled", note: string) {
      const change = { finishedStatus: status, finishedAt: today, finishedBy: who, finishedNote: note };
      const jobs = lines.map((l) => ({
        label: lineName(l),
        key: keyOf(l),
        run: () =>
          chained(keyOf(l), async () => {
            const log = aboutLine(l, {
              action: "finish",
              changes: fieldChange("finishedStatus", "", status),
              note: `Finished as ${status}${note ? `: ${note}` : ""} (gone from A2000)`,
            });
            keep([await put(ROUTING_LINES, (await ensureStored(l, {}, log)).id, change, log)]);
          }),
      }));
      if (await batch("Finishing", jobs)) {
        notify(`${lines.length} line${lines.length === 1 ? "" : "s"} finished`, "They are in History now.");
      }
    },

    /** A new value; a Routing status one with its category (`routing-status.ts`). */
    async addListValue(
      list: AnyListName,
      value: string,
      category?: StatusCategory,
    ): Promise<ListValueRecord | undefined> {
      try {
        const rec = (await track(() =>
          create(
            LIST_VALUES,
            { list, value, retired: "No", ...(category ? { category } : {}) },
            { action: "list", changes: fieldChange("value", "", value), note: `Added “${value}” to ${list}` },
          ),
        )) as unknown as ListValueRecord;
        patch((p) => ({ ...p, listValues: [...p.listValues, rec] }));
        return rec;
      } catch (e) {
        await failed(e);
        return undefined;
      }
    },

    async renameListValue(rec: ListValueRecord, value: string, lines: readonly LineView[]) {
      const moved = relabel(rec.list, [rec.value], value, lines);
      const ok = await batch("Renaming", [
        {
          label: `“${rec.value}” → “${value}”`,
          run: () =>
            put(
              LIST_VALUES,
              rec.id,
              { value },
              { action: "list", changes: fieldChange("value", rec.value, value), note: `${rec.list}: renamed` },
            ),
        },
        ...moved,
      ]);
      if (ok) notify(`Replaced “${rec.value}” with “${value}”`, `${moved.length} line(s) changed`);
    },

    /** A Routing status value's category: what it means to the rules, for every line that holds it. */
    async setListCategory(rec: ListValueRecord, category: StatusCategory) {
      const ok = await batch("Saving", [
        {
          label: rec.value,
          run: () =>
            put(
              LIST_VALUES,
              rec.id,
              { category },
              {
                action: "list",
                changes: fieldChange("category", rec.category ?? "", category),
                note: `${rec.list}: “${rec.value}” is ${category} now`,
              },
            ),
        },
      ]);
      if (ok) notify(`“${rec.value}” is ${category} now`, "Every line with it follows.");
    },

    async retireListValue(rec: ListValueRecord, retired: boolean) {
      const ok = await batch("Saving", [
        {
          label: rec.value,
          run: () =>
            put(
              LIST_VALUES,
              rec.id,
              { retired: retired ? "Yes" : "No" },
              {
                action: "list",
                changes: fieldChange("retired", rec.retired, retired ? "Yes" : "No"),
                note: `${rec.list}: “${rec.value}” ${retired ? "retired" : "restored"}`,
              },
            ),
        },
      ]);
      if (ok) {
        notify(
          retired ? `“${rec.value}” retired` : `“${rec.value}” restored`,
          retired ? "Still shown on old lines, not offered for new picks." : undefined,
        );
      }
    },

    async deleteListValue(rec: ListValueRecord) {
      const log: ChangeSpec = {
        action: "list",
        changes: fieldChange("value", rec.value, ""),
        note: `${rec.list}: deleted “${rec.value}”`,
      };
      if (await batch("Deleting", [{ label: rec.value, run: () => remove(LIST_VALUES, rec.id, log) }])) {
        notify(`Deleted “${rec.value}”`);
      }
    },

    async mergeListValues(target: ListValueRecord, others: readonly ListValueRecord[], lines: readonly LineView[]) {
      const moved = relabel(
        target.list,
        others.map((o) => o.value),
        target.value,
        lines,
      );
      // Lines first, so no line is left holding a value that is gone.
      const ok =
        (await batch("Merging values", moved)) &&
        (await batch(
          "Merging values",
          others.map((o) => ({
            label: `remove “${o.value}”`,
            run: () =>
              remove(LIST_VALUES, o.id, {
                action: "list",
                changes: fieldChange("value", o.value, ""),
                note: `${o.list}: merged “${o.value}” into “${target.value}”`,
              }),
          })),
        ));
      if (ok) notify(`Merged ${others.length} value(s) into “${target.value}”`, `${moved.length} line(s) changed`);
    },

    /** A list's starting values not on it yet; Routing status's seven with their categories. */
    async loadStartingValues(
      list: AnyListName,
      values: readonly (string | { value: string; category: StatusCategory })[],
      existing: readonly ListValueRecord[],
    ) {
      // This list's values only, however they are written.
      const mine = existing.filter((v) => v.list === list);
      const jobs = values
        .map((v) => (typeof v === "string" ? { value: v, category: undefined } : v))
        .filter((v) => !sameListValue(v.value, mine))
        .map(({ value, category }) => ({
          label: value,
          run: () =>
            create(
              LIST_VALUES,
              { list, value, retired: "No", ...(category ? { category } : {}) },
              { action: "list", changes: fieldChange("value", "", value), note: `Starting value of ${list}` },
            ),
        }));
      if (await batch("Loading values", jobs)) notify(`${jobs.length} starting value(s) added to ${list}`);
    },
  };
}
